// frontend-react/src/contexts/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
  useCallback,
} from 'react';
import { authService, User, AuthResponse, PasswordExpiredError } from '../services/authService';
import { message } from 'antd';

// --- 設定常數 ---
const timeoutMins = Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES) || 30;
const IDLE_TIMEOUT = timeoutMins * 60 * 1000;

interface AuthContextType {
  user: User | null;
  loading: boolean;
  mustChangePassword: boolean;

  // ✅ exactOptionalPropertyTypes 正確定義
  passwordRotationDays?: number;

  login: (email: string, password: string, captchaToken?: string, force?: boolean) => Promise<void>;

  register: (email: string, password: string, name: string, captchaToken?: string) => Promise<void>;

  forgotPassword: (email: string, captchaToken?: string) => Promise<void>;

  googleLogin: () => void;
  logout: () => Promise<void>;
  switchCompany: (companyId: number) => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

type SessionInvalidEventDetail = {
  message?: string;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [passwordRotationDays, setPasswordRotationDays] = useState<number | undefined>(undefined);

  // --- 🕒 閒置自動登出 ---
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);

  const resetPasswordFlags = () => {
    setMustChangePassword(false);
    setPasswordRotationDays(undefined);
  };

  const clearAuth = () => {
    authService.removeToken();
    authService.removeUser();
    setUser(null);
    resetPasswordFlags();
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
  };

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      clearAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAutoLogout = useCallback(async () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    if (authService.getToken()) {
      alert('由於您長時間未操作，系統已自動登出。');
      await logout();
      window.location.href = '/login';
    }
  }, [logout]);

  const resetTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (user) {
      idleTimerRef.current = setTimeout(handleAutoLogout, IDLE_TIMEOUT);
    }
  }, [user, handleAutoLogout]);

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    if (user) {
      events.forEach(e => window.addEventListener(e, resetTimer));
      resetTimer();
    }

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, resetTimer]);

  // ✅ 全站：收到「session invalid」事件就立刻清掉登入狀態 + 顯示中性提示
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<SessionInvalidEventDetail>;
      const text =
        ce?.detail?.message ||
        '登入狀態已失效，請重新登入後再試。\n可能原因：登入逾時／已在其他裝置登入／密碼更新後需重新登入。';

      // 1) 清狀態（UI 立刻變未登入）
      clearAuth();

      // 2) 顯示提示（一次即可）
      message.warning(text, 4);
    };

    window.addEventListener('auth:session_invalid', handler as any);
    return () => window.removeEventListener('auth:session_invalid', handler as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- 啟動時檢查登入狀態 ---
  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthStatus = async () => {
    try {
      const savedUser = authService.getUser();
      const savedToken = authService.getToken();

      if (!savedUser || !savedToken) {
        clearAuth();
        return;
      }

      try {
        const valid = await authService.verifyToken();
        if (!valid) {
          clearAuth();
          return;
        }

        const freshUser = await authService.refreshCurrentUser();
        setUser(freshUser || savedUser);
        resetPasswordFlags();
      } catch (e: any) {
        if (e instanceof PasswordExpiredError) {
          setUser(savedUser);
          setMustChangePassword(true);
          setPasswordRotationDays(e.rotationDays);
        } else {
          clearAuth();
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = async (authResponse: AuthResponse) => {
    authService.saveToken(authResponse.token);
    authService.saveUser(authResponse.user);
    setUser(authResponse.user);

    try {
      const freshUser = await authService.refreshCurrentUser();
      if (freshUser) setUser(freshUser);
      resetPasswordFlags();
    } catch (e: any) {
      if (e instanceof PasswordExpiredError) {
        setMustChangePassword(true);
        setPasswordRotationDays(e.rotationDays);
      } else {
        throw e;
      }
    }
  };

  const login = async (email: string, password: string, captchaToken?: string, force = false) => {
    const authResponse = await authService.login(email, password, captchaToken, force);
    await handleAuthSuccess(authResponse);
  };

  const register = async (email: string, password: string, name: string, captchaToken?: string) => {
    const authResponse = await authService.register(email, password, name, captchaToken);
    await handleAuthSuccess(authResponse);
  };

  const forgotPassword = async (email: string, captchaToken?: string) => {
    await authService.forgotPassword(email, captchaToken || '');
  };

  const googleLogin = () => {
    window.location.href = authService.getGoogleLoginUrl();
  };

  const switchCompany = async (companyId: number) => {
    const updatedUser = await authService.switchCompany(companyId);
    setUser(updatedUser);
  };

  const refreshUser = async () => {
    const freshUser = await authService.refreshCurrentUser();
    if (freshUser) setUser(freshUser);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        mustChangePassword,
        login,
        register,
        forgotPassword,
        googleLogin,
        logout,
        switchCompany,
        refreshUser,
        isAuthenticated: !!user,

        // ✅ 只有在有值時才放入（exactOptionalPropertyTypes 正解）
        ...(passwordRotationDays !== undefined ? { passwordRotationDays } : {}),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
